import { useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Carousel as ResponsiveCarousel } from 'react-responsive-carousel'
import { useLocation, useParams } from 'react-router-dom'

import ArrowRight from '@/presentation/assets/svg/ArrowRight.svg'
import CircleInfo from '@/presentation/assets/svg/CircleInfo'
import Button from '@/presentation/components/Button'
import Carousel from '@/presentation/components/Carousel'
import ImageItem from '@/presentation/components/Carousel/components/ImageItem'
import VideoItem from '@/presentation/components/Carousel/components/VideoItem'
import CircleStatus from '@/presentation/components/CircleStatus'
import FullscreenViewer from '@/presentation/components/FullscreenViewer'
import Header from '@/presentation/components/Header'
import RoomItem from '@/presentation/components/RoomItem'
import { divergenceOnboardingStorageKey } from '@/services/constants/localStorageKeys'
import { ROUTES } from '@/services/constants/routes'
import useNavigateWithOrderCode, {
  ILocationState
} from '@/services/hooks/useNavigateWithOrderCode'
import { useAlteration } from '@/services/providers/AlterationContext'
import { Status, useDivergence } from '@/services/providers/DivergenceContext'
import {
  InspectionType,
  useInspection
} from '@/services/providers/InspectionContext'
import { Photo, useRoom, Video } from '@/services/providers/RoomContext'
import checkLocalStorageKeyAndDo from '@/services/utils/checkLocalStorageKeyAndDo'

const RoomItemDetailsPage: React.FC = () => {
  const [showFullscreenViewer, setShowFullscreenViewer] = useState(false)
  const [fullscreenViewerIndex, setFullscreenViewerIndex] = useState(0)
  const [fullscreenViewerPhotos, setFullscreenViewerPhotos] = useState<Photo[]>(
    []
  )
  const [fullscreenViewerVideos, setFullscreenViewerVideos] = useState<Video[]>(
    []
  )

  const { state } = useLocation()
  const alteration = (state as ILocationState)?.alteration ?? false

  const { orderCode, roomId, itemId } = useParams()

  const carouselRef = useRef<ResponsiveCarousel>(null)

  const navigate = useNavigateWithOrderCode()
  const { pathname } = useLocation()

  const { divergences, status, startNewDivergence } = useDivergence()
  const { startNewAlteration } = useAlteration()
  const { handlePutVisitedLabelInRoomItem, rooms } = useRoom()
  const { inspection } = useInspection()

  const { t } = useTranslation()

  const useDamages =
    inspection?.inspectionType === InspectionType.SAIDA ||
    inspection?.inspectionType?.startsWith(InspectionType.CONFERENCIA)

  const room = rooms.find(room => room?.id === roomId)
  const item = room?.items.find(item => item?.id === itemId)

  const carouselPhotos = useMemo(() => {
    if (!item?.photos?.length) return []

    return item?.photos.map(photo => (
      <ImageItem
        key={photo?.filePath}
        filePath={photo?.filePath}
        onMaximizeButtonClick={() => {
          carouselRef?.current &&
            handleOpenFullscreenViewer(
              carouselRef.current?.state?.selectedItem || 0
            )
        }}
      />
    ))
  }, [item?.photos])

  const carouselDamagePhotos = useMemo(() => {
    if (!item?.damagesPhotos?.length) return []

    return item?.damagesPhotos.map(photo => (
      <ImageItem
        key={photo?.filePath}
        filePath={photo?.filePath}
        onMaximizeButtonClick={() => {
          carouselRef?.current &&
            handleOpenFullscreenViewer(
              carouselRef.current?.state?.selectedItem || 0
            )
        }}
      />
    ))
  }, [item?.damagesPhotos])

  const carouselVideos = useMemo(() => {
    if (!item?.videos?.length) return []

    return item?.videos.map(photo => (
      <VideoItem
        key={photo?.filePath}
        filePath={photo?.filePath}
        onMaximizeButtonClick={() => {
          carouselRef?.current &&
            handleOpenFullscreenViewer(
              carouselRef.current?.state?.selectedItem || 0
            )
        }}
      />
    ))
  }, [item?.videos])

  const carouselDamageVideos = useMemo(() => {
    if (!item?.damagesVideos?.length) return []

    return item?.damagesVideos.map(photo => (
      <VideoItem
        key={photo?.filePath}
        filePath={photo?.filePath}
        onMaximizeButtonClick={() => {
          carouselRef?.current &&
            handleOpenFullscreenViewer(
              carouselRef.current?.state?.selectedItem || 0
            )
        }}
      />
    ))
  }, [item?.damagesVideos])

  const handleOpenFullscreenViewer = (index: number) => {
    const rootElement = document.getElementById('root')

    if (rootElement) {
      rootElement.style.filter = 'blur(4px)'
      rootElement.style.position = 'absolute'
      rootElement.style.inset = '0'
    }

    setFullscreenViewerPhotos(
      useDamages ? item?.damagesPhotos || [] : item?.photos || []
    )
    setFullscreenViewerVideos(() => {
      const videos = useDamages ? item?.damagesVideos || [] : item?.videos || []

      return videos.map(video => ({ ...video, isVideo: true }))
    })
    setFullscreenViewerIndex(index)
    setShowFullscreenViewer(true)
  }

  const handleCloseFullscreenViewer = () => {
    const rootElement = document.getElementById('root')

    if (rootElement) {
      rootElement.style.filter = ''
      rootElement.style.position = ''
      rootElement.style.inset = ''
    }

    setShowFullscreenViewer(false)
  }

  const handleNewDivergence = () => {
    room && item && startNewDivergence(room?.id, item?.id)

    if (
      orderCode &&
      checkLocalStorageKeyAndDo(divergenceOnboardingStorageKey(orderCode))
    ) {
      navigate(ROUTES.DIVERGENCE_FORM)

      return
    }

    navigate(ROUTES.DIVERGENCE_TUTORIAL)
  }

  const handleNewAlteration = () => {
    room && item && startNewAlteration(room?.id, item?.id)

    navigate(ROUTES.ALTERATION_FORM)
  }

  const handleGoToDivergences = () => navigate(ROUTES.DIVERGENCE_LIST)

  useEffect(() => {
    carouselRef.current?.moveTo(0)

    return () => {
      handleCloseFullscreenViewer()
    }
  }, [pathname])

  useEffect(() => {
    if (!orderCode || !room?.id || !item?.id) return

    handlePutVisitedLabelInRoomItem(orderCode, room?.id, item?.id)
  }, [orderCode, item?.id, room?.id])

  const carouselContent = useDamages
    ? [...carouselDamageVideos, ...carouselDamagePhotos]
    : [...carouselVideos, ...carouselPhotos]

  const fullscreenViewerContent = [
    ...fullscreenViewerVideos,
    ...fullscreenViewerPhotos
  ]

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <Header
        previousRoute={ROUTES.ROOMS}
        title={`${item?.name} | ${room?.name}`}
      />

      <div className="w-full max-w-[1304px] px-4">
        <div className="my-6 flex flex-wrap items-start justify-between gap-x-12 lg:flex-nowrap">
          <Carousel
            handleCarouselItemClick={handleOpenFullscreenViewer}
            ref={carouselRef}
          >
            {carouselContent}
          </Carousel>

          <section className="flex-1">
            {alteration && (
              <Button
                className="mb-8 mt-4 flex w-full items-center justify-between"
                onClick={handleNewAlteration}
                colored
              >
                <span className="flex-1 text-center">
                  {t('ADD_CHANGE', 'Adicionar alteração')}
                </span>

                <svg
                  width="14"
                  height="14"
                  transform="scale(-1 1)"
                  viewBox="0 0 12 20"
                  fill="none"
                >
                  <path
                    d="M11 1L2 10L3 11L11 19"
                    stroke="white"
                    strokeWidth="2"
                  />
                </svg>
              </Button>
            )}
            <h3 className="mb-6 text-xl text-gray-500">
              {t('OBSERVATIONS', 'Observações')}
            </h3>

            {useDamages ? (
              item?.damages?.length ? (
                item?.damages?.map((damage, i) => (
                  <div
                    key={i}
                    className="mb-4 flex flex-wrap items-start justify-between gap-2 last-of-type:mb-6 sm:flex-nowrap"
                  >
                    <span className="flex-1 text-gray-500">
                      {damage?.description}
                    </span>

                    {inspection?.inspectionType?.startsWith(
                      InspectionType.CONFERENCIA
                    ) && (
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">
                          {t('SITUATION', 'Situação')} -{' '}
                          {!damage?.resolved && t('NO', 'Não')}{' '}
                          {t('RESOLVED', 'Resolvido')}
                        </span>

                        <CircleStatus
                          color={damage?.resolved ? '#059669' : '#CF0000'}
                        />
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <span className="mb-6 block text-gray-500">
                  {t('NO_OBSERVATIONS_RECORDED', 'Nenhuma observação anotada')}
                </span>
              )
            ) : (
              <span className="mb-6 block text-gray-500">
                {item?.observation ||
                  t('NO_OBSERVATIONS_RECORDED', 'Nenhuma observação anotada')}
              </span>
            )}

            {item?.details && (
              <>
                <div className="mb-6 flex flex-wrap items-center justify-between gap-x-2 gap-y-4 sm:flex-nowrap">
                  <h4 className="text-xl text-gray-500">
                    {t('DETAILS', 'Detalhes')}
                  </h4>

                  {!useDamages && (
                    <div className="flex items-center gap-1 text-gray-500">
                      <span>
                        {t('STATE', 'Estado')} - {item?.evaluation?.description}
                      </span>

                      <CircleStatus color={item?.evaluation?.color} />
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  <span className="text-gray-500">
                    {!!item?.details?.length &&
                      item?.details.reduce((acc, item, i) => {
                        const detailsText =
                          acc +
                          (i > 0 ? ' / ' : '') +
                          item?.label +
                          ': ' +
                          item?.value

                        return detailsText
                      }, '')}
                  </span>
                </div>
              </>
            )}

            {inspection?.inspectionType !== 'predial' && (
              <>
                {status === Status.AVAILABLE ? (
                  <>
                    <div className="flex items-center gap-2">
                      <CircleInfo color="#CF0000" className="shrink-0" />

                      <span className="text-error">
                        {t(
                          'SOMETHING_DIFFERENT',
                          'Algo diferente? Faça agora uma contestação'
                        )}
                      </span>
                    </div>

                    <Button
                      className="mb-6 mt-2 w-full max-w-sm"
                      styleType="danger"
                      onClick={handleNewDivergence}
                    >
                      {t('CONTEST', 'Contestar')}
                    </Button>
                  </>
                ) : status === Status.EXPIRED ? (
                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <CircleInfo color="#CF0000" className="shrink-0" />

                      <span className="text-error">
                        {t(
                          'CONTESTATION_EXPIRED',
                          'Prazo para contestação expirado'
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">
                        {t(
                          'CONTACT_ADMINISTRATOR',
                          'Entre em contato com a administradora para habilitar as contestações da vistoria novamente.'
                        )}
                      </span>
                    </div>
                  </div>
                ) : status === Status.UNAVAILABLE ? (
                  <div className="mb-4 flex items-center gap-2">
                    <CircleInfo color="#6b7280" className="shrink-0" />

                    <span className="text-gray-500">
                      {t(
                        'CONTESTATION_NOT_ALLOWED',
                        'Contestação não permitida no momento'
                      )}
                    </span>
                  </div>
                ) : (
                  <div className="mb-4 flex items-center gap-2">
                    <CircleInfo color="#6b7280" className="shrink-0" />

                    <span className="text-gray-500">
                      {t(
                        'CONTESTATION_ALREADY_DONE',
                        'Contestação já realizada'
                      )}
                    </span>
                  </div>
                )}

                {!!divergences?.length && (
                  <div
                    onClick={handleGoToDivergences}
                    className="flex w-full cursor-pointer items-center justify-center gap-x-2 rounded-xl border border-gray-300 bg-[#FAFAFA] p-4 text-primary shadow-sm"
                  >
                    <span className="flex-1 text-center">
                      {t('SEE_CONTESTATIONS_AGAIN', 'Visualizar contestações')}
                    </span>

                    <img src={ArrowRight} alt="Arrow right icon" />
                  </div>
                )}
              </>
            )}
          </section>
        </div>

        <hr />

        <h3 className="mb-4 mt-6 text-lg font-bold text-gray-500">
          {t('NEXT_ITEMS_IN', 'Próximos itens em')}{' '}
          <span className="font-bold text-primary">{room?.name}</span>
        </h3>

        <div className="mb-6 flex gap-6 overflow-x-auto md:flex-wrap">
          {!!room?.items?.length &&
            room.items.map(item => (
              <RoomItem
                alteration={alteration}
                key={item?.id}
                id={item?.id}
                roomId={room?.id || ''}
                image={
                  useDamages ? item?.damagesPhotos[0]?.filePath : item?.image
                }
                name={item?.name}
                photosQuantity={
                  useDamages
                    ? item?.damagesPhotos?.length || 0
                    : item?.photos?.length || 0
                }
                state={item?.state}
                isCurrentItemInView={item?.id === itemId}
                visitedByUser={!!item?.visitedByUser}
                small
              />
            ))}
        </div>
      </div>

      {showFullscreenViewer && (
        <FullscreenViewer
          startIndex={fullscreenViewerIndex}
          content={fullscreenViewerContent}
          onClose={handleCloseFullscreenViewer}
        />
      )}
    </div>
  )
}

export default RoomItemDetailsPage
